import { z } from 'zod'

export enum ContactRelationshipTypeKeyEnum {
  SALES_CUSTOMER = 'sales_customer',
  PRODUCT_USER = 'product_user',
  VENDOR = 'vendor',
  PARTNER = 'partner',
  INVESTOR = 'investor',
  ADVISOR = 'advisor',
  MEDIA = 'media',
  INTERNAL = 'internal',
  COMMUNITY = 'community',
}

export interface ContactRelationshipTypeStageConfig {
  stageId: string
  name: string
  description?: string
  color: string
  order: number
  isDefault?: boolean
  isHidden?: boolean
  stageTypeId: string
}

export interface ContactRelationshipTypeConfig {
  key: ContactRelationshipTypeKeyEnum | string
  name: string
  description?: string
  color: string
  order: number
  isDefault?: boolean
  isSystem?: boolean
  isActive?: boolean
  isHidden?: boolean
  stages: ContactRelationshipTypeStageConfig[]
}

export const DEFAULT_CONTACT_RELATIONSHIP_TYPES: ContactRelationshipTypeConfig[] =
  [
    {
      key: ContactRelationshipTypeKeyEnum.SALES_CUSTOMER,
      name: 'Sales / Customer',
      description: 'Sales and customer relationship lifecycle.',
      color: '#2563EB',
      order: 0,
      isDefault: true,
      stages: [
        {
          stageId: 'new',
          name: 'New',
          description: 'May want to sell to, but no active conversation yet.',
          color: '#F59E0B',
          order: 0,
          isDefault: true,
          stageTypeId: 'NEW',
        },
        {
          stageId: 'prospect',
          name: 'Prospect',
          description:
            'Actively evaluating or in conversation as a potential customer.',
          color: '#3B82F6',
          order: 1,
          stageTypeId: 'PROSPECT',
        },
        {
          stageId: 'customer',
          name: 'Customer',
          description: 'Paying company, account, or buyer relationship.',
          color: '#10B981',
          order: 2,
          stageTypeId: 'CUSTOMER',
        },
        {
          stageId: 'churned_customer',
          name: 'Churned Customer',
          description: 'Former paying customer that is no longer active.',
          color: '#6B7280',
          order: 3,
          stageTypeId: 'CHURNED_CUSTOMER',
        },
      ],
    },
    {
      key: ContactRelationshipTypeKeyEnum.PRODUCT_USER,
      name: 'Product User',
      description: 'A person who has or uses product access.',
      color: '#059669',
      order: 1,
      stages: [
        {
          stageId: 'invited',
          name: 'Invited',
          color: '#60A5FA',
          order: 0,
          isDefault: true,
          stageTypeId: 'INVITED',
        },
        {
          stageId: 'active',
          name: 'Active',
          color: '#10B981',
          order: 1,
          stageTypeId: 'ACTIVE',
        },
        {
          stageId: 'inactive',
          name: 'Inactive',
          color: '#6B7280',
          order: 2,
          stageTypeId: 'INACTIVE',
        },
      ],
    },
    {
      key: ContactRelationshipTypeKeyEnum.VENDOR,
      name: 'Vendor',
      description:
        'A person or company Scrambl pays or depends on operationally.',
      color: '#D97706',
      order: 2,
      stages: [
        {
          stageId: 'potential_vendor',
          name: 'Potential Vendor',
          color: '#F59E0B',
          order: 0,
          isDefault: true,
          stageTypeId: 'POTENTIAL_VENDOR',
        },
        {
          stageId: 'active_vendor',
          name: 'Active Vendor',
          color: '#10B981',
          order: 1,
          stageTypeId: 'ACTIVE_VENDOR',
        },
        {
          stageId: 'preferred_vendor',
          name: 'Preferred Vendor',
          color: '#8B5CF6',
          order: 2,
          stageTypeId: 'PREFERRED_VENDOR',
        },
        {
          stageId: 'inactive',
          name: 'Inactive',
          color: '#6B7280',
          order: 3,
          stageTypeId: 'INACTIVE',
        },
      ],
    },
    {
      key: ContactRelationshipTypeKeyEnum.PARTNER,
      name: 'Partner',
      description:
        'Referral, integration, implementation, channel, or strategic partner.',
      color: '#7C3AED',
      order: 3,
      stages: [
        {
          stageId: 'potential',
          name: 'Potential',
          color: '#F59E0B',
          order: 0,
          isDefault: true,
          stageTypeId: 'POTENTIAL',
        },
        {
          stageId: 'active',
          name: 'Active',
          color: '#10B981',
          order: 1,
          stageTypeId: 'ACTIVE',
        },
        {
          stageId: 'dormant',
          name: 'Dormant',
          color: '#6B7280',
          order: 2,
          stageTypeId: 'DORMANT',
        },
      ],
    },
    {
      key: ContactRelationshipTypeKeyEnum.INVESTOR,
      name: 'Investor',
      description: 'Current or potential investor relationship.',
      color: '#DB2777',
      order: 4,
      stages: [
        {
          stageId: 'target',
          name: 'Target',
          color: '#F59E0B',
          order: 0,
          isDefault: true,
          stageTypeId: 'TARGET',
        },
        {
          stageId: 'in_conversation',
          name: 'In Conversation',
          color: '#3B82F6',
          order: 1,
          stageTypeId: 'IN_CONVERSATION',
        },
        {
          stageId: 'committed',
          name: 'Committed',
          color: '#10B981',
          order: 2,
          stageTypeId: 'COMMITTED',
        },
        {
          stageId: 'passed',
          name: 'Passed',
          color: '#EF4444',
          order: 3,
          stageTypeId: 'PASSED',
        },
      ],
    },
    {
      key: ContactRelationshipTypeKeyEnum.ADVISOR,
      name: 'Advisor',
      description: 'Formal or informal advisory relationship.',
      color: '#0891B2',
      order: 5,
      stages: [
        {
          stageId: 'potential',
          name: 'Potential',
          color: '#F59E0B',
          order: 0,
          isDefault: true,
          stageTypeId: 'POTENTIAL',
        },
        {
          stageId: 'active',
          name: 'Active',
          color: '#10B981',
          order: 1,
          stageTypeId: 'ACTIVE',
        },
        {
          stageId: 'inactive',
          name: 'Inactive',
          color: '#6B7280',
          order: 2,
          stageTypeId: 'INACTIVE',
        },
      ],
    },
    {
      key: ContactRelationshipTypeKeyEnum.MEDIA,
      name: 'Media',
      description: 'Journalists, podcasters, publications, or press contacts.',
      color: '#4F46E5',
      order: 6,
      stages: [
        {
          stageId: 'target',
          name: 'Target',
          color: '#F59E0B',
          order: 0,
          isDefault: true,
          stageTypeId: 'TARGET',
        },
        {
          stageId: 'contacted',
          name: 'Contacted',
          color: '#3B82F6',
          order: 1,
          stageTypeId: 'CONTACTED',
        },
        {
          stageId: 'relationship',
          name: 'Relationship',
          color: '#10B981',
          order: 2,
          stageTypeId: 'RELATIONSHIP',
        },
      ],
    },
    {
      key: ContactRelationshipTypeKeyEnum.INTERNAL,
      name: 'Internal',
      description: 'Employees, contractors, founders, or active collaborators.',
      color: '#334155',
      order: 7,
      stages: [
        {
          stageId: 'active',
          name: 'Active',
          color: '#10B981',
          order: 0,
          isDefault: true,
          stageTypeId: 'ACTIVE',
        },
        {
          stageId: 'former',
          name: 'Former',
          color: '#6B7280',
          order: 1,
          stageTypeId: 'FORMER',
        },
      ],
    },
    {
      key: ContactRelationshipTypeKeyEnum.COMMUNITY,
      name: 'Community',
      description: 'Broader network relationship capital.',
      color: '#16A34A',
      order: 8,
      stages: [
        {
          stageId: 'active',
          name: 'Active',
          color: '#10B981',
          order: 0,
          isDefault: true,
          stageTypeId: 'ACTIVE',
        },
        {
          stageId: 'dormant',
          name: 'Dormant',
          color: '#6B7280',
          order: 1,
          stageTypeId: 'DORMANT',
        },
      ],
    },
  ]

export const ContactRelationshipTypeStageSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  relationshipTypeId: z.string().uuid(),
  stageId: z.string(),
  name: z.string(),
  description: z.string().nullable().optional(),
  color: z.string(),
  order: z.number(),
  isDefault: z.boolean(),
  isHidden: z.boolean(),
  stageTypeId: z.string(),
  createdAt: z.date(),
  updatedAt: z.date(),
})

export type ContactRelationshipTypeStage = z.infer<
  typeof ContactRelationshipTypeStageSchema
>

export const ContactRelationshipTypeDefinitionSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  key: z.string(),
  name: z.string(),
  description: z.string().nullable().optional(),
  color: z.string(),
  order: z.number(),
  isDefault: z.boolean(),
  isSystem: z.boolean(),
  isActive: z.boolean(),
  isHidden: z.boolean(),
  createdAt: z.date(),
  updatedAt: z.date(),
  stages: z.array(ContactRelationshipTypeStageSchema).optional(),
})

export type ContactRelationshipTypeDefinition = z.infer<
  typeof ContactRelationshipTypeDefinitionSchema
>

export const ContactRelationshipTypeAssignmentSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  contactId: z.string().uuid(),
  relationshipTypeId: z.string().uuid(),
  stageId: z.string(),
  isPrimary: z.boolean(),
  createdAt: z.date(),
  updatedAt: z.date(),
  relationshipType: ContactRelationshipTypeDefinitionSchema.optional(),
  stage: ContactRelationshipTypeStageSchema.optional(),
})

export type ContactRelationshipTypeAssignment = z.infer<
  typeof ContactRelationshipTypeAssignmentSchema
>

// A contact's lifecycle assignment as embedded on a contact. GET /contacts
// list rows carry only these required fields; the detail read carries the
// full assignment, type and stage, so the rest are optional here.
export const ContactRelationshipTypeSummarySchema =
  ContactRelationshipTypeDefinitionSchema.partial().required({
    id: true,
    key: true,
    name: true,
    color: true,
  })

export type ContactRelationshipTypeSummary = z.infer<
  typeof ContactRelationshipTypeSummarySchema
>

export const ContactRelationshipTypeStageSummarySchema =
  ContactRelationshipTypeStageSchema.partial().required({
    stageId: true,
    name: true,
    color: true,
    stageTypeId: true,
  })

export const ContactRelationshipTypeAssignmentSummarySchema =
  ContactRelationshipTypeAssignmentSchema.partial()
    .required({
      id: true,
      relationshipTypeId: true,
      stageId: true,
      isPrimary: true,
    })
    .extend({
      relationshipType: ContactRelationshipTypeSummarySchema.optional(),
      stage: ContactRelationshipTypeStageSummarySchema.optional(),
    })

export type ContactRelationshipTypeAssignmentSummary = z.infer<
  typeof ContactRelationshipTypeAssignmentSummarySchema
>

export const CreateContactRelationshipTypeSchema = z.object({
  key: z.string().min(1).max(80),
  name: z.string().min(1).max(120),
  description: z.string().max(500).nullable().optional(),
  color: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/)
    .optional(),
  order: z.number().int().optional(),
  isDefault: z.boolean().optional(),
  isHidden: z.boolean().optional(),
  stages: z
    .array(
      z.object({
        stageId: z.string().min(1).max(80),
        name: z.string().min(1).max(120),
        description: z.string().max(500).nullable().optional(),
        color: z
          .string()
          .regex(/^#[0-9a-fA-F]{6}$/)
          .optional(),
        order: z.number().int().optional(),
        isDefault: z.boolean().optional(),
        isHidden: z.boolean().optional(),
        stageTypeId: z.string().min(1).max(80).optional(),
      }),
    )
    .optional(),
})

export type CreateContactRelationshipType = z.infer<
  typeof CreateContactRelationshipTypeSchema
>

export const UpdateContactRelationshipTypeSchema =
  CreateContactRelationshipTypeSchema.omit({
    key: true,
    stages: true,
  }).partial()

export type UpdateContactRelationshipType = z.infer<
  typeof UpdateContactRelationshipTypeSchema
>

export const SetContactRelationshipTypeStagesSchema = z.object({
  stages: z.array(
    z.object({
      stageId: z.string().min(1).max(80),
      name: z.string().min(1).max(120),
      description: z.string().max(500).nullable().optional(),
      color: z
        .string()
        .regex(/^#[0-9a-fA-F]{6}$/)
        .optional(),
      order: z.number().int().optional(),
      isDefault: z.boolean().optional(),
      isHidden: z.boolean().optional(),
      stageTypeId: z.string().min(1).max(80).optional(),
    }),
  ),
  deletedStageAction: z
    .enum(['migrate_assignments', 'remove_assignments'])
    .optional(),
  stageMigrationMap: z.record(z.string(), z.string()).optional(),
})

export type SetContactRelationshipTypeStages = z.infer<
  typeof SetContactRelationshipTypeStagesSchema
>

export const ArchiveContactRelationshipTypeSchema = z.object({
  replacementRelationshipTypeId: z.string().uuid().optional(),
  removeAssignments: z.boolean().optional(),
})

export type ArchiveContactRelationshipType = z.infer<
  typeof ArchiveContactRelationshipTypeSchema
>

export const ContactRelationshipTypeUsageSchema = z.object({
  relationshipTypeId: z.string().uuid(),
  assignmentCount: z.number().int(),
  stages: z.array(
    z.object({
      stageId: z.string(),
      name: z.string(),
      color: z.string(),
      assignmentCount: z.number().int(),
    }),
  ),
})

export type ContactRelationshipTypeUsage = z.infer<
  typeof ContactRelationshipTypeUsageSchema
>

export const AssignContactRelationshipTypeSchema = z.object({
  relationshipTypeId: z.string().uuid().optional(),
  relationshipTypeKey: z.string().optional(),
  stageId: z.string().optional(),
  isPrimary: z.boolean().optional(),
})

export type AssignContactRelationshipType = z.infer<
  typeof AssignContactRelationshipTypeSchema
>

export const UpdateContactRelationshipTypeAssignmentSchema = z.object({
  stageId: z.string().optional(),
  isPrimary: z.boolean().optional(),
})

export type UpdateContactRelationshipTypeAssignment = z.infer<
  typeof UpdateContactRelationshipTypeAssignmentSchema
>
